import React, { useState } from 'react';
import { MEMBERSHIP_TIERS } from '../types/membership';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useMembership } from '../hooks/useMembership';
import './MembershipUpgrade.css';

interface MembershipUpgradeProps {
  currentTier?: string;
  onUpgrade?: (tierId: string, sport?: 'pickleball' | 'tennis' | 'golf') => void;
  onSuccess?: () => void;
  sport?: 'pickleball' | 'tennis' | 'golf';
  compact?: boolean;
}

export const MembershipUpgrade: React.FC<MembershipUpgradeProps> = ({
  currentTier = 'free',
  onUpgrade,
  onSuccess,
  sport,
  compact = false,
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { upgradeMembership } = useMembership(user?.id || '');
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const getSportSpecificTiers = () => {
    let result = MEMBERSHIP_TIERS;
    if (sport) {
      result = result.filter(tier =>
        tier.id === 'free' ||
        tier.id === 'pro' ||
        tier.id === `premium-${sport}`
      );
    }
    if (compact) {
      result = result.filter(tier => tier.id !== 'free');
    }
    return result;
  };

  const tiers = getSportSpecificTiers();

  const handleUpgrade = async (tierId: string) => {
    if (tierId === 'free') return;

    if (!user) {
      window.location.href = '/signin?redirect=/pricing';
      return;
    }

    // Allow parent override (e.g. for free tier sign-up flow)
    if (onUpgrade) {
      onUpgrade(tierId, sport);
      return;
    }

    setLoadingTier(tierId);
    setError(null);
    console.log('💳 Starting checkout for tier:', tierId, 'user:', user.email);

    try {
      // In dev, use VITE_API_BASE_URL if set (e.g. https://yoursite.vercel.app) to bypass proxy issues
      const apiBase = import.meta.env.VITE_API_BASE_URL || '';
      const url = `${apiBase}/api/create-checkout-session`.replace(/\/+/g, '/');
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tierId, userId: user.id, email: user.email }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.url) {
        throw new Error(data.error || `Failed to start checkout (${res.status})`);
      }

      window.location.href = data.url;
    } catch (err: any) {
      // Dev fallback: simulate upgrade when API unreachable (e.g. CORS, network)
      if (import.meta.env.DEV) {
        console.log('🛠️ Dev mode: simulating upgrade (API unreachable)');
        upgradeMembership(tierId, sport);
        setLoadingTier(null);
        onSuccess?.();
        return;
      }
      const message = err.message || 'Something went wrong. Please try again.';
      setError(message);
      setLoadingTier(null);
      console.error('Checkout error:', err);
    }
  };

  return (
    <div className="membership-upgrade">
      <div className="membership-header">
        <h2>Choose Your Plan</h2>
        <p>Get professional video analysis for less than a personal coach!</p>
      </div>

      {!compact && <div className="pricing-comparison">
        <div className="comparison-card">
          <h3>Personal Coach</h3>
          <div className="price">$50-150/hour</div>
          <ul>
            <li>Limited availability</li>
            <li>No video analysis</li>
            <li>Requires scheduling</li>
            <li>No progress tracking</li>
          </ul>
        </div>

        <div className="vs">VS</div>

        <div className="comparison-card highlight">
          <h3>Our Platform</h3>
          <div className="price">$5/video</div>
          <ul>
            <li>24/7 availability</li>
            <li>Professional video analysis</li>
            <li>Instant results</li>
            <li>Progress tracking</li>
          </ul>
        </div>
      </div>}

      <div className="membership-tiers">
        {tiers.map((tier) => (
          <div 
            key={tier.id} 
            className={`membership-tier ${tier.id === currentTier ? 'current' : ''} ${tier.id === 'pro' ? 'featured' : ''}`}
            style={{
              borderColor: tier.id === 'pro' ? theme.colors.accent : theme.colors.border
            }}
          >
            {tier.id === 'pro' && <div className="featured-badge">Most Popular</div>}
            
            <div className="tier-header">
              <h3>{tier.name}</h3>
              <div className="tier-price">
                {tier.price > 0 ? (
                  <>
                    <span className="price">${tier.price}</span>
                    <span className="period">/month</span>
                  </>
                ) : (
                  <span className="price">Free</span>
                )}
              </div>
            </div>

            <div className="video-analysis">
              <div className="included-videos">
                {tier.includedVideos} video{tier.includedVideos !== 1 ? 's' : ''} included
              </div>
              {tier.additionalVideoPrice > 0 && (
                <div className="additional-price">
                  Additional videos: ${tier.additionalVideoPrice} each
                </div>
              )}
            </div>

            <ul className="tier-features">
              {tier.features.map((feature, index) => (
                <li key={index}>✅ {feature}</li>
              ))}
            </ul>

            <button
              className={`upgrade-button ${tier.id === currentTier ? 'current' : ''}`}
              onClick={() => handleUpgrade(tier.id)}
              disabled={tier.id === currentTier || loadingTier === tier.id}
              style={{
                backgroundColor: tier.id === currentTier ? theme.colors.secondary : theme.colors.primary,
                color: theme.colors.background
              }}
            >
              {loadingTier === tier.id ? 'Redirecting...' :
               tier.id === currentTier ? 'Current Plan' :
               tier.price === 0 ? 'Get Started' : 'Upgrade Now'}
            </button>
          </div>
        ))}
      </div>

      {error && (
        <div className="upgrade-error" style={{ color: 'red', textAlign: 'center', margin: '1rem 0' }}>
          {error}
        </div>
      )}

      <div className="membership-footer">
        <p>💡 <strong>Value Proposition:</strong> 5 analyses included per month—less than one private lesson. Extra analyses just $5 each.</p>
        <p>🔄 <strong>Flexible:</strong> Premium includes 5/month. Need more? Pay $5 per additional analysis. No long-term commitments.</p>
        <p>📈 <strong>Progress Tracking:</strong> See your improvement over time with detailed analytics.</p>
      </div>
    </div>
  );
};












